import { type CSSProperties, type ReactNode, useLayoutEffect, useRef } from "react";
import { useGlobals } from "../../lib/dashboard-api";
import { CountUp } from "./count-up";
import { affixGapEm, pct } from "./helpers";
import { C, FS, FU, T, W } from "./tokens";

/* ── The 1.1 hero strip — one navy band that IS the section's headline, not a
      band + a redundant card row. It carries the flagship metrics as big serif
      count-ups over a gold-glow navy gradient, columns split by hairlines, blue
      kicker. `HeroBand` is the shared, data-agnostic band (Global 1.1 AND Saudi
      1.1 both render it — one component, never a fork); `HeroRow` is the Global
      binding that feeds it the /globals hero. Each column carries either a pair
      of YoY / vs-reference deltas OR a single sub-note. */

/** Tiny stat in the metric footer — neutral label, red/green value by sign; a
 *  null value (the server could not compute a defensible change) prints a dash
 *  rather than a green +0.0%. */
function DeltaStat({ label, value }: { label: string; value: number | null }) {
  return (
    <div>
      <div
        style={{
          fontFamily: FU,
          fontSize: T.tag,
          fontWeight: W.semibold,
          color: C.textMd,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
      <div
        style={{
          marginTop: 3,
          fontFamily: FU,
          fontSize: T.body,
          fontWeight: W.semibold,
          color: value == null ? C.textLo : value >= 0 ? C.emerging : C.crisis,
          fontFeatureSettings: '"tnum" 1',
        }}
      >
        {value == null ? "—" : pct(value)}
      </div>
    </div>
  );
}

export interface HeroBandMetric {
  key: string;
  label: string;
  /** Reporting year / period shown top-right of the column. */
  year?: number | string;
  /** Numeric value for the count-up. */
  value: number;
  decimals?: number;
  prefix?: string;
  unit?: string;
  format?: (n: number) => string;
  /** Accent colour for the unit affix. */
  accent?: string;
  /** Two-up delta footer (e.g. YoY + vs 2019). Takes precedence over `note`. */
  deltas?: { label: string; value: number | null }[];
  /** Single sub-note footer (e.g. "spend ÷ visitors"). */
  note?: { text: string; color?: string };
  /** Provenance for THIS column's number, printed under its footer. Served by
   *  the route, never authored here — see the provenance tests. */
  source?: string;
  /** A one-line caveat on THIS column's number (coverage, basis), printed under
   *  its source line so it stays with the tile it qualifies. */
  caveat?: string;
  /** Grid-column span (default 1) — widen a column, e.g. to hold a chart. */
  span?: number;
  /** Custom column body rendered in place of the number/deltas (e.g. a bar
   *  chart). The label head still renders above it. */
  custom?: ReactNode;
}

function HeroBandColumn({ m, showNote }: { m: HeroBandMetric; showNote: boolean }) {
  return (
    <div className="glo-col" style={m.span && m.span > 1 ? { gridColumn: `span ${m.span}` } : undefined}>
      {/* Label + reporting year. The year rides INLINE at the end of the label
          text (a dimmer token), so it flows and wraps with the words — it can
          never detach into an awkward top-right corner or spill past the column
          edge, whatever the label's length. The row's min-height (auto-reserved
          on the band) keeps every column's number on one baseline. */}
      <div className="glo-col-head">
        <span className="glo-col-label">
          {m.label}
          {m.year != null ? <span className="glo-col-year"> {m.year}</span> : null}
        </span>
      </div>

      {m.custom != null ? <div className="glo-col-custom">{m.custom}</div> : null}
      {m.custom != null ? null : (
        <>
          <div className="glo-col-num">
            {m.prefix ? <span className="glo-col-affix">{m.prefix}</span> : null}
            <span className="glo-col-big">
              <CountUp value={m.value} decimals={m.decimals ?? 0} format={m.format} />
            </span>
            {/* Nearly every unit that comes through this span takes a real space
                — words ("nights", "destinations"), scales ("B", "T") and percent
                alike. Only a suffix that binds to the number ("/100", "x", "°")
                stays tight, so the gap is decided per-affix rather than by the
                row's 2px flex gap alone. The PREFIX above keeps the tight gap
                unconditionally — "$" and "SAR" lead the number. */}
            {m.unit ? (
              <span
                className="glo-col-affix"
                style={{ color: m.accent ?? C.textMd, marginLeft: `${affixGapEm(m.unit)}em` }}
              >
                {m.unit}
              </span>
            ) : null}
          </div>

          {m.deltas && m.deltas.length > 0 ? (
            <div className="glo-col-deltas">
              {m.deltas.map((d) => (
                <DeltaStat key={d.label} label={d.label} value={d.value} />
              ))}
            </div>
          ) : showNote ? (
            // Note footer (with its divider line) renders only when SOME column in the
            // band has note text — empty columns keep a spacer so numbers stay aligned.
            // When NO column has a note, HeroBand passes showNote=false and the whole
            // footer is dropped (no stray line / dead space).
            <div className="glo-col-note" style={{ color: m.note?.color ?? C.textMd }}>
              {m.note?.text || " "}
            </div>
          ) : null}
          {m.source ? <div className="glo-col-source">Source: {m.source}</div> : null}
          {m.caveat ? <div className="glo-col-source">{m.caveat}</div> : null}
        </>
      )}
    </div>
  );
}

/** The shared navy headline band. Data-agnostic — callers pass the kicker,
    the metric columns, and an optional footnote line under the band. */
export function HeroBand({
  kicker,
  metrics,
  footnote,
  headMinHeight,
  collapse = true,
}: {
  /** Blue eyebrow over the band. Optional — omit it for merged stat strips whose
   *  context already comes from the section nav (only the original Global 1.1
   *  design carries a band eyebrow, "2025 · Global demand"). */
  kicker?: ReactNode;
  metrics: HeroBandMetric[];
  footnote?: ReactNode;
  /** A FLOOR for the uniform label-box height (px). The band auto-reserves the
   *  tallest label's height on its own (see below); pass this only to force a
   *  larger minimum. Numbers stay baseline-aligned regardless. */
  headMinHeight?: number;
  /** When false, the band keeps all columns on ONE row at every width instead of
   *  responsively collapsing to 2 / 1. The signal-summary strips (always 3 short
   *  count columns) pass this so they never orphan a column onto a second row. */
  collapse?: boolean;
}) {
  // Rule A — every column's number sits on ONE baseline. Labels wrap to different
  // line-counts (and the floated year can push a wide first word to a third
  // line), so we RESERVE the tallest header's height across the row and let the
  // shorter ones keep negative space above their number. Measured live rather
  // than hardcoded, so it holds at any font, column-count breakpoint, or zoom.
  const gridRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure whenever the metrics identity changes
  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const floor = headMinHeight ?? 0;
    const equalize = () => {
      const heads = grid.querySelectorAll<HTMLElement>(".glo-col-head");
      if (!heads.length) return;
      // Clear the reservation first so each head reports its NATURAL height.
      grid.style.setProperty("--glo-head-min", "0px");
      let max = floor;
      for (const h of heads) max = Math.max(max, h.getBoundingClientRect().height);
      grid.style.setProperty("--glo-head-min", `${Math.ceil(max)}px`);
    };
    equalize();
    // Re-equalize on viewport/zoom/font changes (not via a ResizeObserver on the
    // grid itself — writing the var back would retrigger it in a loop).
    window.addEventListener("resize", equalize);
    document.fonts?.ready.then(equalize).catch(() => {});
    return () => window.removeEventListener("resize", equalize);
  }, [metrics, headMinHeight]);
  return (
    <>
      <div className="glo-hero">
        {kicker != null && kicker !== "" ? (
          <div className="glo-hero-kick">
            <span>{kicker}</span>
          </div>
        ) : null}
        <div
          ref={gridRef}
          className={`glo-hero-grid${collapse ? "" : " no-collapse"}`}
          style={
            {
              "--cols": metrics.reduce((s, m) => s + (m.span ?? 1), 0),
              ...(headMinHeight ? { "--glo-head-min": `${headMinHeight}px` } : {}),
            } as CSSProperties
          }
        >
          {metrics.map((m) => (
            <HeroBandColumn key={m.key} m={m} showNote={metrics.some((x) => x.note?.text)} />
          ))}
        </div>
        <style>{GLO_HERO_STYLES}</style>
      </div>
      {footnote != null ? (
        <div
          style={{ fontFamily: FU, fontSize: T.cap, color: C.textMd, lineHeight: 1.5, marginTop: 12, marginBottom: 44 }}
        >
          {footnote}
        </div>
      ) : null}
    </>
  );
}

export function HeroRow() {
  const { data } = useGlobals();
  const hero = data?.hero ?? null;
  const latestYear = data?.latestYear;
  const refYear = data?.referenceYear ?? 2019;

  if (!hero || latestYear === null || latestYear === undefined) return null;

  const stay = hero.lengthOfStay;
  // A change is labelled by the year it actually compares against: the server
  // holds length of stay to years with enough paired reporters, so "YoY" may
  // legitimately span more than one calendar year.
  const stayYoyLabel =
    stay.yoyBasis && stay.year != null && stay.yoyBasis.year !== stay.year - 1 ? `vs ${stay.yoyBasis.year}` : "YoY";
  const stayRefLabel = stay.vs2019Basis ? `vs ${stay.vs2019Basis.year}` : `vs ${refYear}`;

  const metrics: HeroBandMetric[] = [
    {
      key: "arrivals",
      label: "International arrivals",
      value: hero.arrivals.value / 1000,
      decimals: 2,
      unit: "B",
      accent: C.arrivals,
      year: hero.arrivals.year ?? latestYear,
      deltas: [
        { label: "YoY", value: hero.arrivals.yoy },
        { label: `vs ${refYear}`, value: hero.arrivals.vs2019 },
      ],
      source: data?.sources?.arrivals,
    },
    {
      key: "receipts",
      label: "Tourism receipts",
      value: hero.receipts.value / 1000,
      decimals: 2,
      prefix: "$",
      unit: "T",
      // goldText, not gold: identical #ffbf00 on dark, but flips to a legible
      // dark amber on light (bare gold #ffbf00 is invisible on the pale band).
      accent: C.goldText,
      year: hero.receipts.year ?? latestYear,
      deltas: [
        { label: "YoY", value: hero.receipts.yoy },
        { label: `vs ${refYear}`, value: hero.receipts.vs2019 },
      ],
      source: data?.sources?.receipts,
    },
    {
      key: "spv",
      label: "Spend per visitor",
      value: hero.spendPerVisitor.value,
      decimals: 0,
      prefix: "$",
      accent: C.spv,
      year: hero.spendPerVisitor.year ?? latestYear,
      deltas: [
        { label: "YoY", value: hero.spendPerVisitor.yoy },
        { label: `vs ${refYear}`, value: hero.spendPerVisitor.vs2019 },
      ],
      source: data?.sources?.spendPerVisitor,
    },
    {
      key: "stay",
      label: "Average length of stay",
      value: stay.value,
      decimals: 1,
      unit: "nights",
      // Neutral accent — green is reserved for positive deltas.
      accent: C.textMd,
      year: stay.year ?? latestYear,
      deltas: [
        { label: stayYoyLabel, value: stay.yoy },
        { label: stayRefLabel, value: stay.vs2019 },
      ],
      source: data?.sources?.lengthOfStay ?? undefined,
      caveat:
        stay.countries != null && stay.year != null
          ? `Based on ${stay.countries} reporting countries, ${stay.year}`
          : undefined,
    },
  ];

  return <HeroBand kicker={`${latestYear} · Global demand`} metrics={metrics} />;
}

const GLO_HERO_STYLES = `
  /* Navy hero strip — the shared 1.4/1.5 gradient (gold glow top-right over a
     navy fall), opaque over the shell so it anchors the section. */
  .glo-hero {
    position: relative;
    overflow: hidden;
    border: 1px solid ${C.border};
    border-radius: 12px;
    padding: 24px 30px 26px;
    background:
      radial-gradient(circle at 88% 6%, rgba(255, 191, 0, 0.12), transparent 32%),
      linear-gradient(135deg, #07101d, #071a4f 58%, #031035);
    box-shadow: var(--shadow-card-rest);
  }
  .glo-hero-kick {
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: ${FU};
    font-size: ${T.micro}px;
    font-weight: ${W.semibold};
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: #82BAFF;
    margin-bottom: 22px;
  }
  .glo-hero-grid {
    display: grid;
    /* minmax(0,1fr) — equal, shrinkable columns so a wide metric can't push the
       grid past the panel (plain 1fr = minmax(auto,1fr) overflows on min-content).
       Column count is driven by --cols so the band flexes to 4 (Global) or 5
       (Saudi) metrics; the media queries below still collapse it responsively. */
    grid-template-columns: repeat(var(--cols, 4), minmax(0, 1fr));
    gap: 0;
  }
  .glo-col {
    min-width: 0;
    padding: 0 18px;
    border-left: 1px solid rgba(255, 255, 255, 0.09);
  }
  .glo-col:first-child {
    padding-left: 0;
    border-left: none;
  }
  .glo-col:last-child {
    padding-right: 0;
  }
  .glo-col-head {
    /* Block (not flex) so the label text can wrap AROUND the floated year. */
    display: block;
    /* Reserve a uniform label-box height so every column's number starts at the
       same baseline regardless of how many lines its label wraps to. Driven by a
       var so a denser band (5 narrow cols → 3-line labels) can reserve more
       without touching the 4-col Global default. */
    min-height: var(--glo-head-min, 30px);
  }
  .glo-col-label {
    font-family: ${FU};
    font-size: ${T.micro}px;
    font-weight: ${W.semibold};
    color: ${C.textMd};
    letter-spacing: 0.1em;
    text-transform: uppercase;
    line-height: 1.3;
  }
  .glo-col-year {
    /* Inline reporting-year token trailing the label — a dimmer shade so it
       reads as an annotation, kept on one piece (nowrap) so "2025" never breaks
       mid-number. It flows with the words, so it can't detach or overflow. */
    color: ${C.textLo};
    white-space: nowrap;
  }
  .glo-col-num {
    display: flex;
    align-items: baseline;
    gap: 2px;
    margin-top: 12px;
    font-family: ${FS};
    color: ${C.textHi};
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .glo-col-big {
    font-size: 40px;
    font-weight: ${W.semibold};
    letter-spacing: -0.025em;
    white-space: nowrap;
  }
  .glo-col-affix {
    font-size: 16px;
    font-weight: ${W.semibold};
    color: ${C.textMd};
  }
  .glo-col-deltas {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.09);
  }
  .glo-col-note {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.09);
    font-family: ${FU};
    font-size: ${T.cap}px;
    font-weight: ${W.semibold};
    line-height: 1.4;
  }
  .glo-col-source {
    /* The per-number provenance the client asked for: a quiet italic line under
       each column's footer, so a reader never has to work out which of two
       publishers a given tile belongs to. */
    margin-top: 12px;
    font-family: ${FU};
    font-size: ${T.tag}px;
    font-style: italic;
    line-height: 1.4;
    color: ${C.textLo};
  }
  /* A wider column's custom body (e.g. a bar chart), aligned under the label
     head where the count-up number would otherwise sit. */
  .glo-col-custom { margin-top: 14px; }
  @media (max-width: 900px) {
    .glo-hero-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px 0; }
    .glo-col { padding: 0 22px; }
    .glo-col:nth-child(odd) { padding-left: 0; border-left: none; }
    .glo-col:nth-child(even) { padding-right: 0; }
  }
  @media (max-width: 520px) {
    .glo-hero-grid { grid-template-columns: 1fr; }
    .glo-col { padding: 20px 0 0; border-left: none; border-top: 1px solid rgba(255,255,255,0.09); }
    .glo-col:first-child { padding-top: 0; border-top: none; }
    .glo-col-big { font-size: 38px; }
  }
  /* no-collapse — the signal-summary strips keep all columns on one row at every
     width. Higher specificity than the media rules above, so it wins without
     !important and the strip never orphans a column onto a second row. */
  .glo-hero-grid.no-collapse { grid-template-columns: repeat(var(--cols, 3), minmax(0, 1fr)); gap: 0; }
  .glo-hero-grid.no-collapse .glo-col { padding: 0 18px; border-left: 1px solid rgba(255,255,255,0.09); border-top: none; }
  .glo-hero-grid.no-collapse .glo-col:first-child { padding-left: 0; border-left: none; }
  .glo-hero-grid.no-collapse .glo-col:last-child { padding-right: 0; }

  /* ── Light theme — the navy headline flips to the shared warm-sunrise hero
        surface (--hero-surface: bright white→blue sweep + warm gold glow +
        lift), the ONE light hero look across 1.1/1.2/1.3/1.4. Every rule here is
        scoped to [data-theme="light"] so the dark band above stays byte-
        identical. The text, unit affixes and deltas already re-tint via their
        tokens (C.textHi/textMd/textLo, the series vars, C.goldText); these rules
        only recolour the things hardcoded-for-dark above — the surface, the
        white hairlines, and the pale-sky kicker. It stays LIGHT but leads: the
        brightest/warmest surface on the page, so the dulled cards recede. ── */
  [data-theme="light"] .glo-hero {
    border-color: var(--hero-border);
    background: var(--hero-surface);
    box-shadow: var(--hero-shadow);
  }
  /* Kicker: the light twin of the dark band's fixed #82BAFF sky-blue — darkened
     to clear AA as a small mono label on the pale panel. A fixed blue (NOT
     var(--color-accent), which the workspace shells remap to their own hue) so
     the shared band's kicker stays one blue across every workspace, matching the
     dark band above and the italic heading accent. */
  [data-theme="light"] .glo-hero-kick { color: #2b5fb0; }
  [data-theme="light"] .glo-col { border-left-color: rgba(0, 15, 71, 0.10); }
  [data-theme="light"] .glo-col-deltas,
  [data-theme="light"] .glo-col-note { border-top-color: rgba(0, 15, 71, 0.10); }
  [data-theme="light"] .glo-hero-grid.no-collapse .glo-col { border-left-color: rgba(0, 15, 71, 0.10); }
  @media (max-width: 520px) {
    [data-theme="light"] .glo-col { border-top-color: rgba(0, 15, 71, 0.10); }
  }
`;
